import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  activityTypes,
  employmentTypes,
  flatRateTaxes,
  personalKeyGroups,
} from 'modules/contracts/hooks/useFetchGermanContractData'
import {
  getActivityType,
  getEmploymentType,
  getFlatRateTax,
  getPersonalKeyGroup,
} from 'modules/contracts/lib/germanCountrySpecifics'
import getFieldKey from 'modules/contracts/lib/getFieldKey'

export interface Update {
  key: string
  previousValue?: string | number | Date | null | undefined
  currentValue?: string | number | Date | null | undefined
}

interface Props {
  update: Update
}

const formatKey = (key: string) => {
  return getFieldKey(key)
}

const formatValue = (value: string | number | Date | null | undefined, key: string) => {
  if (value instanceof Date) return dateToString(moment(value), 'ui')

  if (key === 'de_personal_key_group_id') {
    const personalKeyGroup = getPersonalKeyGroup(
      personalKeyGroups(),
      typeof value === 'number' ? value : undefined
    )
    if (personalKeyGroup) {
      return personalKeyGroup.label
    }
  }

  if (key === 'de_employment_type') {
    const employmentType = getEmploymentType(
      employmentTypes(),
      typeof value === 'number' ? value : undefined
    )
    if (employmentType) {
      return employmentType.label
    }
  }

  if (key === 'de_flat_rate_tax') {
    const flatRateTax = getFlatRateTax(
      flatRateTaxes(),
      typeof value === 'number' ? value : undefined
    )
    if (flatRateTax) {
      return flatRateTax.label
    }
  }

  if (key === 'de_activity_type') {
    const activityType = getActivityType(
      activityTypes(),
      typeof value === 'number' ? value : undefined
    )
    if (activityType) {
      return activityType.label
    }
  }

  return value || ''
}

const DetailedUpdate = ({ update }: Props) => {
  const hasCurrentValue = !!update.currentValue
  const hasPreviousValue = !!update.previousValue

  return (
    <Box>
      <Text.Normal size="100" weight="medium" color="grey800">
        <Text.Normal size="100" weight="medium">
          {formatKey(update.key)}:&nbsp;
        </Text.Normal>
        {hasPreviousValue && (
          <>
            <Text.Normal size="100" stroked>
              {formatValue(update.previousValue, update.key)}
            </Text.Normal>
            {hasCurrentValue && (
              <Box style={{ display: 'inline-block' }} paddingX="s4">
                <Text.Normal size="100" weight="bold">
                  {'->'}
                </Text.Normal>
              </Box>
            )}
          </>
        )}
        <Text.Normal size="100" weight="bold">
          {formatValue(update.currentValue, update.key) + '.'}
        </Text.Normal>
      </Text.Normal>
    </Box>
  )
}

const ContractVersionUpdate = ({ update }: Props) => {
  const isDetailedChange = update.currentValue || update.previousValue

  return (
    <Box component="li" style={{ display: 'list-item', listStyleType: 'disc' }}>
      {isDetailedChange && <DetailedUpdate update={update} />}
      {!isDetailedChange && (
        <Text.Normal size="100" weight="medium">
          {formatKey(update.key)}:&nbsp;
          <Text.Normal size="100" weight="bold">
            {i18n.t('contract_versions.navigator.cards.changed')}
          </Text.Normal>
        </Text.Normal>
      )}
    </Box>
  )
}

export default observer(ContractVersionUpdate)
